import React from 'react';
import { Button, Card, toast } from '@heroui/react';
import FeaturesMarkup from '@components/plans/features-markup';
import PlanBadge from '@components/plans/plan-badge';
import { ActivePlan, BuyPlan, CancelSubscription, PlanInfo } from '@/types';
import { sendDataAPI } from '@lib/sendDataAPI';
import { useSession } from '@context/sessionProvider';

interface PaidPlanCardProps {
	plan: PlanInfo;
	isActive: boolean;
	load(): Promise<void>;
	setIsLoading(isLoading: boolean): void;
	activePlan: ActivePlan | undefined;
}

const PaidPlanCard: React.FC<PaidPlanCardProps> = ({ plan, isActive, load, setIsLoading, activePlan }) => {
	const sessionData = useSession();

	const PAID_PLAN_ID = 2;
	const buyClick = async () => {
		let response: BuyPlan = await sendDataAPI(
			{ jwtToken: sessionData.context },
			'/api/plan/buy',
			'POST',
			JSON.stringify({
				redirectUrl: document.referrer,
				planId: PAID_PLAN_ID,
			}),
		);
		response = await response.json();

		if (response?.data?.url && response?.data?.needRedirect && window.top) {
			window.top.location.href = response.data.url;
		} else {
			toast.danger('Plan', {
				description: `An error occurred, please try again: ${response.message}`,
			});
		}
	};

	const cancelPlan = async () => {
		setIsLoading(true);

		let response: CancelSubscription = await sendDataAPI(
			{ jwtToken: sessionData.context },
			'/api/plan/cancel',
			'DELETE',
		);
		response = await response.json();

		if (response.data === 'Cancelled') {
			setTimeout(() => {
				updateInfo().catch((error) => {
					console.error(error);
				});
			}, 3000);
		} else {
			toast.danger('Plan', {
				description: `An error occurred, please try again: ${response.message}`,
			});
			setIsLoading(false);
		}
	};

	const reactivatePlan = async () => {
		setIsLoading(true);

		let response: CancelSubscription = await sendDataAPI(
			{ jwtToken: sessionData.context },
			'/api/plan/re-activate',
			'POST',
		);
		response = await response.json();

		if (response.data === 'Done') {
			setTimeout(() => {
				updateInfo().catch((error) => {
					console.error(error);
				});
			}, 3000);
		} else {
			toast.danger('Plan', {
				description: `An error occurred, please try again: ${response.message}`,
			});
			setIsLoading(false);
		}
	};

	const updateInfo = async () => {
		await load();
		setIsLoading(false);
	};

	return (
		<Card>
			<Card.Header>
				<PlanBadge plan={plan} isActive={isActive} />
			</Card.Header>
			<Card.Content>
				<p className='text-xl font-bold mb-5'>${plan.price}/monthly</p>
				<div className='flex mt-[6px]'>
					<div className='mb-[6px] h-[350px]'>
						<FeaturesMarkup planDescription={plan.description} />
					</div>
				</div>
				<div className='flex space-x-2'>
					{activePlan?.is_free && (
						<Button
							variant='primary'
							onPress={() => {
								buyClick().catch((error) => {
									console.error(error);
								});
							}}
						>
							Subscribe
						</Button>
					)}

					{!activePlan?.is_free && !activePlan?.expired_at && (
						<Button variant='primary' onPress={cancelPlan}>
							Cancel
						</Button>
					)}

					{activePlan?.expired_at !== null && !activePlan?.is_free && (
						<Button variant='primary' onPress={reactivatePlan}>
							Reactivate
						</Button>
					)}
				</div>
			</Card.Content>
		</Card>
	);
};

export default PaidPlanCard;
